import React, { useMemo } from "react";
import { useAdmin } from "@/context/AdminContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line,
} from "recharts";
import { format, subDays } from "date-fns";
import { fr } from "date-fns/locale";

const PIE_COLORS = ["#94a3b8", "#f59e0b", "#22c55e"];

const AdminAnalyticsPage = () => {
  const { quotes, contacts } = useAdmin();

  const statusData = useMemo(() => {
    const counts = { Nouveau: 0, "En cours": 0, Traité: 0 };
    quotes.forEach((q) => {
      const s = q.status || "nouveau";
      if (s === "traite") counts["Traité"] += 1;
      else if (s === "en_cours") counts["En cours"] += 1;
      else counts["Nouveau"] += 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [quotes]);

  const serviceData = useMemo(() => {
    const counts = {};
    quotes.forEach((q) => {
      const s = q.data?.service_category || q.data?.service_type || "Autre";
      counts[s] = (counts[s] || 0) + 1;
    });
    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, count]) => ({ name, count }));
  }, [quotes]);

  const lineData = useMemo(() => {
    const days = Array.from({ length: 30 }, (_, i) => {
      const d = subDays(new Date(), 29 - i);
      return { key: format(d, "yyyy-MM-dd"), day: format(d, "dd/MM", { locale: fr }), devis: 0, contacts: 0 };
    });
    quotes.forEach((q) => {
      const e = days.find((d) => d.key === (q.created_at || "").slice(0, 10));
      if (e) e.devis += 1;
    });
    contacts.forEach((c) => {
      const e = days.find((d) => d.key === (c.created_at || "").slice(0, 10));
      if (e) e.contacts += 1;
    });
    return days;
  }, [quotes, contacts]);

  const treated = quotes.filter((q) => q.status === "traite").length;
  const rate = quotes.length ? Math.round((treated / quotes.length) * 100) : 0;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total devis", value: quotes.length },
          { label: "Devis traités", value: treated },
          { label: "Taux traitement", value: `${rate}%` },
        ].map(({ label, value }) => (
          <div key={label} className="bg-white rounded-2xl border border-slate-200 p-5">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-blue">{label}</p>
            <p className="font-heading font-extrabold text-3xl text-slate-900 mt-2">{value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-5">
        <p className="font-heading font-bold text-slate-900 mb-4">Évolution sur 30 jours</p>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={lineData}>
            <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={4} />
            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={20} />
            <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }} />
            <Line type="monotone" dataKey="devis" name="Devis" stroke="#166534" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="contacts" name="Contacts" stroke="#0284c7" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-heading font-bold text-slate-900 mb-4">Devis par service</p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={serviceData} layout="vertical">
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis dataKey="name" type="category" width={90} tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }} />
              <Bar dataKey="count" name="Devis" fill="#166534" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-5">
          <p className="font-heading font-bold text-slate-900 mb-4">Répartition par statut</p>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, percent }) => `${name} ${Math.round(percent * 100)}%`} labelLine={false}>
                {statusData.map((_, i) => (
                  <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0", fontSize: "12px" }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
